import {
  Badge,
  Button,
  Checkbox,
  createSortableColumn,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  type TanstackReactTable,
} from "@devhop/ui";
import { Check, Eye, MoreVertical, Pencil, XCircle } from "lucide-react";
import type { OvertimeRequestListItem } from "@/modules/hr/overtime/domain/types";
import { formatDateWithWeekdayLao } from "@/shared/lib/date-time";

const STATUS_LABEL: Record<OvertimeRequestListItem["status"], string> = {
  pending: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
};

type OTApprovalListTableProps = {
  data: OvertimeRequestListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onViewDetail?: (row: OvertimeRequestListItem) => void;
  onEdit?: (row: OvertimeRequestListItem) => void;
  onApprove: (row: OvertimeRequestListItem) => void;
  onReject: (row: OvertimeRequestListItem) => void;
  /** Bulk: only allowed when all selected have the same status (pending). */
  selectedIds?: string[];
  onSelectedIdsChange?: (ids: string[]) => void;
  onBulkApprove?: (ids: string[]) => void;
  onBulkReject?: (ids: string[]) => void;
};

export function OTApprovalListTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onViewDetail,
  onEdit,
  onApprove,
  onReject,
  selectedIds = [],
  onSelectedIdsChange,
  onBulkApprove,
  onBulkReject,
}: OTApprovalListTableProps) {
  const enableSelection = onSelectedIdsChange != null;
  const selectableIds = data.filter((r) => r.status === "pending").map((r) => r.id);
  const allPageSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
  const somePageSelected = selectableIds.some((id) => selectedIds.includes(id));
  const setSelection = (ids: string[]) => onSelectedIdsChange?.(ids);
  const toggleRow = (id: string) => {
    if (selectedIds.includes(id)) setSelection(selectedIds.filter((x) => x !== id));
    else setSelection([...selectedIds, id]);
  };
  const toggleAll = () => {
    if (allPageSelected) setSelection(selectedIds.filter((id) => !selectableIds.includes(id)));
    else setSelection([...new Set([...selectedIds, ...selectableIds])]);
  };
  const selectedRows = data.filter((r) => selectedIds.includes(r.id));
  const firstSelected = selectedRows[0];
  const allSelectedSameStatus =
    selectedRows.length > 0 &&
    firstSelected != null &&
    selectedRows.every((r) => r.status === firstSelected.status);
  const allSelectedPending = selectedRows.length > 0 && selectedRows.every((r) => r.status === "pending");
  const canBulkApprove = allSelectedPending && onBulkApprove;
  const canBulkReject = allSelectedPending && onBulkReject;

  const columns: TanstackReactTable.ColumnDef<OvertimeRequestListItem>[] = [
    ...(enableSelection
      ? [
          {
            id: "select",
            header: () => (
              <div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-input/60 bg-muted/40">
                <Checkbox
                  checked={
                    selectableIds.length === 0
                      ? false
                      : allPageSelected
                        ? true
                        : somePageSelected
                          ? "indeterminate"
                          : false
                  }
                  onCheckedChange={selectableIds.length === 0 ? undefined : toggleAll}
                  disabled={selectableIds.length === 0}
                  aria-label="ເລືອກທັງໝົດ (ລໍຖ້າອະນຸມັດ)"
                  className="size-4"
                />
              </div>
            ),
            cell: ({ row }) => {
              const isPending = row.original.status === "pending";
              return (
                <div
                  className={
                    isPending
                      ? "flex shrink-0 size-9 items-center justify-center rounded-md border border-input/60 bg-muted/40 transition-colors hover:bg-muted/60"
                      : "flex shrink-0 size-9 items-center justify-center rounded-md border border-input/60 bg-muted/20 opacity-60 transition-colors"
                  }
                >
                  <Checkbox
                    checked={selectedIds.includes(row.original.id)}
                    onCheckedChange={isPending ? () => toggleRow(row.original.id) : undefined}
                    disabled={!isPending}
                    aria-label={isPending ? `ເລືອກ ${row.original.employeeName}` : undefined}
                    className="size-4"
                  />
                </div>
              );
            },
            size: 44,
            enableSorting: false,
          } as TanstackReactTable.ColumnDef<OvertimeRequestListItem>,
        ]
      : []),
    createSortableColumn<OvertimeRequestListItem>("employeeName", "ພະນັກງານ", {
      size: 180,
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.employeeName}</div>
          <div className="text-muted-foreground text-xs">{row.original.employeeEmail}</div>
        </div>
      ),
    }),
    createSortableColumn<OvertimeRequestListItem>("workDate", "ວັນທີ", {
      size: 160,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateWithWeekdayLao(row.original.workDate)}
        </span>
      ),
    }),
    createSortableColumn<OvertimeRequestListItem>("startTime", "ເວລາເລີ່ມ – ສິ້ນສຸດ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {row.original.startTime} – {row.original.endTime}
        </span>
      ),
    }),
    createSortableColumn<OvertimeRequestListItem>("hours", "ຊົ່ວໂມງ", {
      size: 80,
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {row.original.hours.toFixed(1)} ຊມ.
        </span>
      ),
    }),
    createSortableColumn<OvertimeRequestListItem>("reason", "ເຫດຜົນ", {
      size: 160,
      cell: ({ row }) => (
        <span className="block max-w-[200px] truncate text-muted-foreground text-sm">
          {row.original.reason ?? "—"}
        </span>
      ),
    }),
    createSortableColumn<OvertimeRequestListItem>("status", "ສະຖານະ", {
      size: 90,
      cell: ({ row }) => (
        <Badge variant="secondary">{STATUS_LABEL[row.original.status]}</Badge>
      ),
    }),
    {
      id: "actions",
      header: "ການດຳເນີນການ",
      cell: ({ row }) => {
        const isPending = row.original.status === "pending";
        const r = row.original;
        return (
          <div className="flex items-center gap-2">
            {isPending && (
              <>
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => onApprove(r)}
                  aria-label="ອະນຸມັດ"
                >
                  ອະນຸມັດ
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onReject(r)}
                  aria-label="ປະຕິເສດ"
                >
                  ປະຕິເສດ
                </Button>
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" className="size-8 p-0" aria-label="ເມນູ">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onViewDetail && (
                  <DropdownMenuItem onClick={() => onViewDetail(r)}>
                    <Eye className="mr-2 h-4 w-4" />
                    ເບິ່ງລາຍລະອຽດ
                  </DropdownMenuItem>
                )}
                {isPending && onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(r)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    ແກ້ໄຂ
                  </DropdownMenuItem>
                )}
                {isPending && (
                  <>
                    <DropdownMenuItem onClick={() => onApprove(r)}>
                      <Check className="mr-2 h-4 w-4" />
                      ອະນຸມັດ
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onReject(r)}>
                      <XCircle className="mr-2 h-4 w-4" />
                      ປະຕິເສດ
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      size: 220,
    },
  ];

  return (
    <>
        {enableSelection && selectedIds.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground text-sm">
              ເລືອກ {selectedIds.length} ລາຍການ
              {!allSelectedSameStatus && " — ເລືອກໄດ້ແຕ່ລາຍການສະຖານະດຽວກັນ (ລໍຖ້າອະນຸມັດ) ເພື່ອອະນຸມັດ/ປະຕິເສດພ້ອມກັນ"}
            </span>
            {canBulkApprove && (
              <Button size="sm" onClick={() => onBulkApprove(selectedIds)}>
                ອະນຸມັດທີ່ເລືອກ
              </Button>
            )}
            {canBulkReject && (
              <Button size="sm" variant="outline" onClick={() => onBulkReject(selectedIds)}>
                ປະຕິເສດທີ່ເລືອກ
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setSelection([])}>
              ລ້າງການເລືອກ
            </Button>
          </div>
        )}
        <DataTable<OvertimeRequestListItem, unknown>
          noDataMessage="ບໍ່ພົບຄຳຂໍລໍຖ້າອະນຸມັດ."
          isLoading={isLoading}
          columns={columns}
          data={data}
          offset={offset}
          limit={limit}
          totalCount={totalCount}
          onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortingChange={(sorting) => {
            if (sorting[0]?.id === "") return;
            onSortingChange(sorting[0]?.id ?? "workDate", !!sorting[0]?.desc);
          }}
        />
    </>
  );
}
